import GuideLayout, {
  H2,
  H3,
  P,
  UL,
  OL,
  Callout,
  ExternalLink,
  Strong,
  Em,
} from "../ui/GuideLayout.jsx";
import { Link } from "react-router-dom";

export default function RemessasGuide() {
  return (
    <GuideLayout
      title="Como mandar dinheiro para o Brasil"
      subtitle="Comparativo honesto entre Wise, Brastel, GoRemit e bancos. Quem realmente entrega mais reais por iene."
      slug="mandar-dinheiro-para-o-brasil"
      description="Guia completo sobre remessas Japão-Brasil. Comparação entre Wise, Brastel, GoRemit e bancos tradicionais. Taxas, câmbio e implicações fiscais."
      updated="Maio de 2026"
      readTime="9 min"
    >
      <P>
        Tem dois jeitos de perder dinheiro mandando remessa pro Brasil:
        pagar tarifa alta, ou pagar câmbio ruim. Quase todo mundo presta
        atenção só na tarifa visível e ignora o câmbio — onde os bancos
        e algumas remessadoras escondem o maior custo.
      </P>
      <P>
        Este guia mostra como comparar de verdade, qual serviço usar
        para cada situação, e o que ninguém te conta sobre remessa em
        valor alto.
      </P>

      <Callout>
        <Strong>Atalho:</Strong> Use{" "}
        <Link to="/calculadoras/remessas" className="text-accent underline">
          o comparador de remessas
        </Link>{" "}
        para ver agora quanto cada serviço entrega para o valor que você
        quer mandar.
      </Callout>

      <H2 id="anatomia">A anatomia do custo de uma remessa</H2>
      <P>
        Quando você manda ¥100.000 para o Brasil, três coisas afetam o
        valor que chega:
      </P>
      <OL>
        <li>
          <Strong>Tarifa fixa</Strong> — o valor visível. Wise: ¥500-¥800.
          Brastel: ~¥880. Banco tradicional: ¥3.000-¥7.000.
        </li>
        <li>
          <Strong>Spread do câmbio</Strong> — a margem que a remessadora
          adiciona em cima da cotação real (mid-market). É o que dá lucro
          escondido. Varia de 0,4% (Wise) a 3-4% (banco tradicional).
        </li>
        <li>
          <Strong>Taxa do banco receptor no Brasil</Strong> — alguns
          bancos brasileiros cobram tarifa de recebimento internacional
          (R$ 15 a R$ 80, dependendo do banco). Conta poupança em
          algumas instituições não cobra.
        </li>
      </OL>
      <P>
        Para entender o impacto: numa remessa de ¥1 milhão, um spread de
        3% custa <Strong>¥30.000</Strong>. Mais caro do que qualquer
        tarifa fixa.
      </P>

      <H2 id="comparativo">Comparativo dos principais serviços</H2>

      <H3>Wise (antigo TransferWise)</H3>
      <P>
        Câmbio mais próximo do real (spread tipicamente abaixo de 0,5%).
        Tarifa fixa baixa + porcentagem proporcional. Transferência
        geralmente cai em 1 dia útil.
      </P>
      <UL>
        <li>
          <Strong>Melhor para:</Strong> valores médios a altos
          (¥100.000+), pessoas que comparam câmbio.
        </li>
        <li>
          <Strong>Pontos fracos:</Strong> sem suporte presencial em
          português, interface só em japonês ou inglês, ainda não tem
          opção de pagamento via konbini.
        </li>
        <li>
          <Strong>Como pagar:</Strong> débito direto da conta japonesa
          (Furikomi). Não aceita cartão de crédito para reais.
        </li>
      </UL>

      <H3>Brastel Remit</H3>
      <P>
        Atendimento em português, presença forte na comunidade
        brasileira, várias formas de pagar (konbini, ATM, etc).
      </P>
      <UL>
        <li>
          <Strong>Melhor para:</Strong> quem prefere atendimento em
          português, quem manda valores pequenos a médios regularmente.
        </li>
        <li>
          <Strong>Pontos fracos:</Strong> câmbio menos vantajoso que o
          Wise (spread típico ~1-1,5%). Tarifa fixa um pouco maior.
        </li>
        <li>
          <Strong>Como pagar:</Strong> várias opções incluindo konbini.
        </li>
      </UL>

      <H3>GoRemit (SMBC)</H3>
      <P>
        Serviço de banco tradicional (Sumitomo Mitsui), com algumas
        vantagens sobre o banco comum mas ainda mais caro que opções
        modernas.
      </P>
      <UL>
        <li>
          <Strong>Melhor para:</Strong> quem já é cliente SMBC e quer
          tudo num lugar só.
        </li>
        <li>
          <Strong>Pontos fracos:</Strong> tarifa fixa de ¥2.000, câmbio
          com spread maior (2%+).
        </li>
      </UL>

      <H3>Banco japonês comum (MUFG, SMBC, Mizuho)</H3>
      <P>
        A opção mais cara em quase todos os cenários. Tarifa fixa de
        ¥4.500-¥7.000, spread cambial de 3-4%, prazo de 3-5 dias úteis.
      </P>
      <Callout kind="warn">
        Usar banco tradicional só se faz sentido para <Em>valores muito
        altos</Em> (¥10 milhões+) onde você precisa de comprovante
        oficial bancário, ou se você não tem outra opção.
      </Callout>

      <H2 id="exemplo-pratico">Exemplo prático: ¥200.000 mensais</H2>
      <P>
        Considere mandar ¥200.000 todo mês para a família por 12 meses.
        Total: ¥2,4 milhões enviados em um ano.
      </P>
      <UL>
        <li>
          <Strong>Wise:</Strong> spread 0,45%, tarifa total ¥1.500.
          Custo total anual: <Strong>~¥28.800</Strong>.
        </li>
        <li>
          <Strong>Brastel:</Strong> spread 1,5%, tarifa ¥880. Custo
          total anual: <Strong>~¥46.500</Strong>.
        </li>
        <li>
          <Strong>Banco:</Strong> spread 3,5%, tarifa ¥4.500. Custo
          total anual: <Strong>~¥138.000</Strong>.
        </li>
      </UL>
      <P>
        Diferença Wise vs banco: <Strong>~¥109.000/ano</Strong>. Quase
        um mês de aluguel.
      </P>

      <H2 id="valores-altos">Valores altos: o que ninguém te conta</H2>

      <H3>Limite operacional</H3>
      <P>
        Cada serviço tem limites. Wise: até ¥30 milhões por transação
        para o Brasil (mas pode pedir documentação extra acima de ¥1
        milhão). Brastel: ¥3 milhões por transação. Banco: sem limite
        prático mas pode pedir comprovação de origem dos fundos.
      </P>

      <H3>Documentação de origem dos fundos</H3>
      <P>
        Acima de ¥1 milhão por transferência, quase todo serviço vai
        pedir comprovação de origem do dinheiro (extrato bancário
        mostrando a renda). É procedimento padrão de prevenção a
        lavagem.
      </P>

      <H3>Implicações fiscais no Brasil</H3>
      <Callout kind="warn">
        <Strong>Atenção:</Strong> remessas regulares para o Brasil
        podem gerar obrigação de declaração no IR brasileiro, mesmo se
        você não é residente fiscal brasileiro. O destinatário no
        Brasil precisa declarar como rendimento recebido do exterior
        em determinados casos.
      </Callout>
      <P>
        Não confunda: você no Japão, se tributário fiscal japonês, não
        tem obrigação no Brasil pelo dinheiro recebido aqui. Mas a
        pessoa que recebe lá pode ter. Consulte um contador brasileiro
        especializado em residentes no exterior para sua situação
        específica.
      </P>

      <H2 id="estrategias">Estratégias inteligentes</H2>

      <H3>1. Combine câmbio favorável com remessa programada</H3>
      <P>
        Wise permite criar &ldquo;rate alert&rdquo; — você define um
        câmbio-meta e recebe notificação quando atinge. Útil para
        mandar valor grande no melhor momento.
      </P>

      <H3>2. Use conta multi-moeda para timing</H3>
      <P>
        Wise oferece conta multi-moeda gratuita. Você pode converter
        JPY para BRL na conta antes de transferir, congelando o
        câmbio. Útil quando você acha que o real vai cair contra o
        iene.
      </P>

      <H3>3. Considere transferências menos frequentes mas maiores</H3>
      <P>
        Tarifa fixa pesa menos em valores grandes. Mandar ¥1 milhão
        uma vez é mais econômico que ¥250 mil quatro vezes (em
        serviços com tarifa fixa significativa).
      </P>

      <H3>4. Atenção ao banco receptor</H3>
      <P>
        Bradesco, Itaú e Santander cobram tarifa de recebimento
        internacional. Caixa Econômica e algumas contas digitais
        (Nubank, Inter) não cobram. Pesquise antes de definir a conta
        de destino.
      </P>

      <H2 id="evitar">O que evitar</H2>
      <UL>
        <li>
          <Strong>Western Union/MoneyGram</Strong>: prático mas câmbio
          ruim e tarifa alta. Use só em emergência absoluta.
        </li>
        <li>
          <Strong>Câmbio de rua, sacoleira, &ldquo;amigo que leva&rdquo;</Strong>:
          informalidade traz risco (perda, fraude). E se o valor for
          alto, você pode ter problema com a Receita Federal brasileira
          por origem não-rastreável.
        </li>
        <li>
          <Strong>Acumular dinheiro em casa para mandar depois</Strong>:
          além do risco de assalto e desvalorização, mandar ¥1 milhão de
          uma vez em dinheiro físico para o Brasil é problema com
          alfândega.
        </li>
      </UL>

      <H2 id="conclusao">Resumo prático</H2>
      <OL>
        <li>
          Para a maioria dos casos: <Strong>Wise</Strong> é a opção mais
          econômica.
        </li>
        <li>
          Para quem prefere português e konbini: <Strong>Brastel</Strong>.
        </li>
        <li>
          Para valores muito altos com necessidade bancária formal:
          considerar <Strong>GoRemit ou banco</Strong> só se houver
          razão específica.
        </li>
        <li>
          Sempre confira o câmbio efetivo, não só a tarifa.
        </li>
        <li>
          Use o comparador antes de cada remessa grande — o melhor
          serviço pode variar com o momento e o valor.
        </li>
      </OL>
    </GuideLayout>
  );
}
